import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import downloadDocument from 'garaje/utils/download-document';

import { type ProfileDocument, getStatusDisplay, formatUploadDate } from './document-status';

interface DocumentCardSignature {
  Args: {
    document: ProfileDocument;
    onOpen?: (document: ProfileDocument) => void;
  };
  Element: HTMLDivElement;
}

export default class DocumentCard extends Component<DocumentCardSignature> {
  @tracked imageFailedToLoad = false;

  get hasPreview(): boolean {
    return !!this.args.document.downloadUrl;
  }

  // Anonymized documents may still have a non-null downloadUrl; treat
  // anonymizedAt as the authoritative "do not open" signal.
  get isDiscarded(): boolean {
    return !!this.args.document.anonymizedAt;
  }

  @action handleImageError(): void {
    this.imageFailedToLoad = true;
  }

  get statusConfig(): { label: string; bgClass: string; textClass: string } | null {
    return getStatusDisplay(this.args.document.approvalStatus);
  }

  get subtitle(): string | undefined {
    return formatUploadDate(this.args.document.createdAt);
  }

  @action
  openAndClose(close: () => void): void {
    close();
    this.args.onOpen?.(this.args.document);
  }

  @action
  downloadAndClose(close: () => void): void {
    close();
    const { downloadUrl, title } = this.args.document;
    if (!downloadUrl) return;
    void downloadDocument(downloadUrl, title);
  }

  openPreview = (): void => {
    this.args.onOpen?.(this.args.document);
  };

  <template>
    {{! template-lint-disable use-ui-card }}
    <div
      class="bg-white border border-carbon-20 rounded-lg overflow-hidden px-3 py-5 flex flex-col gap-4"
      data-test-document-card={{@document.id}}
      ...attributes
    >
      {{! Header: title + three-dot menu }}
      <div class="flex items-start justify-between gap-2">
        <div class="flex flex-col gap-2 min-w-0 flex-1">
          <p class="text-sm font-semibold text-carbon-70 leading-[1.25] m-0 truncate" data-test-document-title>
            {{@document.title}}
          </p>
          {{#if this.subtitle}}
            <p class="text-xs text-carbon-40 leading-[1.25] m-0 truncate" data-test-document-description>
              {{this.subtitle}}
            </p>
          {{/if}}
          {{#if this.statusConfig}}
            <span
              class="inline-flex items-center self-start px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-[1px] leading-none
                {{this.statusConfig.bgClass}}
                {{this.statusConfig.textClass}}"
              data-test-document-status
            >
              {{this.statusConfig.label}}
            </span>
          {{/if}}
        </div>

        {{! Three-dot menu }}
        <div class="shrink-0">
          <BasicDropdown as |dropdown|>
            <dropdown.Trigger>
              <button
                type="button"
                class="flex items-center justify-center w-[28px] h-[28px] bg-carbon-5 rounded-lg text-carbon-40 hover:text-carbon-60 hover:bg-carbon-10 transition-colors"
                aria-label="Document actions"
                data-test-document-menu-trigger
              >
                {{svgJar "horizontal-dots" width="18" height="4" class="block shrink-0"}}
              </button>
            </dropdown.Trigger>
            <dropdown.Content>
              <div class="bg-white rounded-2xl shadow-md p-1 min-w-[180px] -mt-[115px] -right-[24px] relative">
                <button
                  type="button"
                  class="flex items-center gap-2 w-full px-2 py-2 text-base text-carbon-70 rounded-lg hover:bg-carbon-5 transition-colors"
                  data-test-document-open
                  {{on "click" (fn this.openAndClose dropdown.actions.close)}}
                >
                  {{svgJar "expand-corners" width="14" height="14" class="shrink-0"}}
                  <span>Open</span>
                </button>
                <button
                  type="button"
                  class="flex items-center gap-2 w-full px-2 py-2 text-base rounded-lg transition-colors
                    {{if
                      this.isDiscarded
                      'text-carbon-40 cursor-not-allowed opacity-60'
                      'text-carbon-70 hover:bg-carbon-5'
                    }}"
                  data-test-document-download
                  disabled={{this.isDiscarded}}
                  {{on "click" (fn this.downloadAndClose dropdown.actions.close)}}
                >
                  {{svgJar "download" width="16" height="16" class="shrink-0"}}
                  <span>Download</span>
                </button>
              </div>
            </dropdown.Content>
          </BasicDropdown>
        </div>
      </div>

      {{! Image preview only — non-image docs (PDFs) open in the modal on
          click. An inline iframe at signed S3 URLs trips Chrome enterprise
          URL-blocklists and disables DevTools on the whole host page. }}
      {{#if this.isDiscarded}}
        <div
          class="relative w-full aspect-square bg-carbon-5 border border-carbon-10 rounded-[4px] overflow-hidden flex items-center justify-center"
          data-test-document-discarded
        >
          <span class="text-carbon-40 text-sm font-normal italic">Document discarded</span>
        </div>
      {{else}}
        <button
          type="button"
          class="relative block w-full aspect-square bg-black border border-carbon-10 rounded-[4px] overflow-hidden p-0 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gem-60"
          aria-label="Preview {{@document.title}}"
          data-test-document-card-preview-trigger
          {{on "click" this.openPreview}}
        >
          {{#if this.hasPreview}}
            {{#if this.imageFailedToLoad}}
              <div
                class="w-full h-full flex flex-col items-center justify-center bg-carbon-5 gap-2"
                data-test-document-placeholder="non-image"
              >
                {{svgJar "document-placeholder" width="32" height="40" class="text-carbon-20"}}
                <span class="text-carbon-30 text-xs">Click to preview</span>
              </div>
            {{else}}
              <img
                src={{@document.downloadUrl}}
                alt={{@document.title}}
                class="w-full h-full object-contain"
                data-test-document-preview
                {{on "error" this.handleImageError}}
              />
            {{/if}}
          {{else}}
            <div
              class="w-full h-full flex flex-col items-center justify-center bg-carbon-5 gap-2"
              data-test-document-placeholder="no-preview"
            >
              {{svgJar "document-placeholder" width="32" height="40" class="text-carbon-20"}}
              <span class="text-carbon-30 text-xs">No preview</span>
            </div>
          {{/if}}
        </button>
      {{/if}}
    </div>
  </template>
}
